import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Upload, X, Image as ImageIcon, Camera } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  primaryText,
  secondaryText,
  smallTextSize,
  baseTextSize,
} from '../../../../styling/stylingHelper';
import { Recipe, getTranslatedText, SupportedLanguage } from '../../../../services/recipes/recipesService';

interface RecipeImageCarouselProps {
  recipe: Recipe;
  onSaveImages: (imageFiles: File[]) => Promise<void>;
}

const RecipeImageCarousel = ({ recipe, onSaveImages }: RecipeImageCarouselProps) => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isManagingImages, setIsManagingImages] = useState(false);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [savingImages, setSavingImages] = useState(false);

  const hasImages = recipe.images && recipe.images.length > 0;

  const handlePreviousImage = () => {
    if (!recipe.images || recipe.images.length === 0) return;
    setCurrentImageIndex((prev) => (prev === 0 ? recipe.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!recipe.images || recipe.images.length === 0) return;
    setCurrentImageIndex((prev) => (prev === recipe.images.length - 1 ? 0 : prev + 1));
  };

  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setNewImages((prev) => [...prev, ...filesArray]);
    }
  };

  const handleRemoveNewImage = (index: number) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveImages = async () => {
    if (newImages.length === 0) return;

    try {
      setSavingImages(true);
      await onSaveImages(newImages);
      setNewImages([]);
      setIsManagingImages(false);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Failed to save images:', error);
    } finally {
      setSavingImages(false);
    }
  };

  const handleCancelManageImages = () => {
    setNewImages([]);
    setIsManagingImages(false);
  };

  if (isManagingImages) {
    return (
      <div className={cn(
        "bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark",
        "p-6 mb-6"
      )}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
            <h3 className={cn("text-[18px] leading-6 font-semibold", primaryText)}>
              {t('recipes.manageImages')}
            </h3>
          </div>
          <button
            onClick={handleCancelManageImages}
            className={cn(
              "p-2 rounded-lg",
              "hover:bg-[#F2F4F7] dark:hover:bg-zinc-800 transition-colors"
            )}
          >
            <X className="w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
          </button>
        </div>

        {/* Current Images */}
        {hasImages && (
          <div className="mb-6">
            <p className={cn(smallTextSize, secondaryText, 'mb-3 font-medium')}>
              {t('recipes.currentImages')}
            </p>
            <div className="flex gap-3 flex-wrap">
              {recipe.images.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url}
                    alt={`Recipe ${index + 1}`}
                    className="w-24 h-24 object-cover rounded-lg border border-[#E5E7EB] dark:border-zinc-700"
                  />
                </div>
              ))}
            </div>
            <p className={cn('text-xs mt-3', secondaryText)}>
              {t('recipes.uploadImagesReplaceNote')}
            </p>
          </div>
        )}

        {/* New Images Preview */}
        {newImages.length > 0 && (
          <div className="mb-6">
            <p className={cn(smallTextSize, secondaryText, 'mb-3 font-medium')}>
              {t('recipes.newImagesToUpload')}
            </p>
            <div className="flex gap-3 flex-wrap">
              {newImages.map((file, index) => (
                <div key={index} className="relative group">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`New ${index + 1}`}
                    className="w-24 h-24 object-cover rounded-lg border border-[#E5E7EB] dark:border-zinc-700"
                  />
                  <button
                    onClick={() => handleRemoveNewImage(index)}
                    className={cn(
                      "absolute -top-2 -right-2 p-1 rounded-full",
                      "bg-[#D92D20] dark:bg-[#D92D20] text-white dark:text-white",
                      "hover:bg-[#B42318] transition-colors",
                      "shadow-sm"
                    )}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageFileSelect}
          className="hidden"
        />
        <div className="flex gap-3">
          <button
            onClick={() => imageInputRef.current?.click()}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-xl",
              "border border-[#D0D5DD] dark:border-borderPrimary-dark",
              "text-[#344054] dark:text-textSecondary-dark",
              "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
              "text-sm font-semibold"
            )}
          >
            <Upload className="w-4 h-4" />
            <span>{t('recipes.uploadImages')}</span>
          </button>

          {newImages.length > 0 && (
            <button
              onClick={handleSaveImages}
              disabled={savingImages}
              className={cn(
                "px-4 py-2.5 rounded-xl",
                "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
                "hover:bg-[#047857] transition-colors",
                "text-sm font-semibold",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {savingImages ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-6 rounded-xl overflow-hidden">
      {hasImages ? (
        <>
          <img
            src={recipe.images[currentImageIndex]}
            alt={`${getTranslatedText(recipe.nameTranslations, lang)} - ${currentImageIndex + 1}`}
            className="w-full h-64 min-[650px]:h-80 min-[1050px]:h-96 object-cover"
          />

          {/* Gradient overlay for better button visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 dark:from-black/30 via-transparent dark:via-transparent to-black/10 dark:to-black/10" />

          {/* Manage Images Button Overlay */}
          <button
            onClick={() => setIsManagingImages(true)}
            className={cn(
              "absolute top-4 right-4",
              "flex items-center gap-2 px-3 py-2",
              "bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm",
              "text-[#344054] dark:text-textSecondary-dark rounded-lg",
              "hover:bg-white dark:hover:bg-zinc-900 transition-colors",
              "text-sm font-medium",
              "shadow-sm"
            )}
          >
            <ImageIcon className="w-4 h-4" />
            <span>{t('recipes.manageImages')}</span>
          </button>

          {recipe.images.length > 1 && (
            <>
              {/* Navigation Buttons */}
              <button
                onClick={handlePreviousImage}
                className={cn(
                  "absolute left-3 top-1/2 -translate-y-1/2",
                  "w-10 h-10 rounded-full",
                  "bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm",
                  "text-[#344054] dark:text-textSecondary-dark",
                  "hover:bg-white dark:hover:bg-zinc-900 transition-colors",
                  "flex items-center justify-center",
                  "shadow-sm"
                )}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNextImage}
                className={cn(
                  "absolute right-3 top-1/2 -translate-y-1/2",
                  "w-10 h-10 rounded-full",
                  "bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm",
                  "text-[#344054] dark:text-textSecondary-dark",
                  "hover:bg-white dark:hover:bg-zinc-900 transition-colors",
                  "flex items-center justify-center",
                  "shadow-sm"
                )}
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Image indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {recipe.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={cn(
                      'w-2.5 h-2.5 rounded-full transition-all',
                      index === currentImageIndex
                        ? 'bg-white dark:bg-white scale-110'
                        : 'bg-white/50 dark:bg-white/50 hover:bg-white/70'
                    )}
                  />
                ))}
              </div>

              {/* Image counter */}
              <div className={cn(
                "absolute bottom-4 right-4",
                "px-3 py-1.5 rounded-lg",
                "bg-black/50 dark:bg-black/50 backdrop-blur-sm",
                "text-white dark:text-white text-sm font-medium"
              )}>
                {currentImageIndex + 1} / {recipe.images.length}
              </div>
            </>
          )}
        </>
      ) : (
        <div className={cn(
          "relative w-full h-64 min-[650px]:h-80 min-[1050px]:h-96",
          "bg-gradient-to-br from-[#F2F4F7] to-[#E4E7EC] dark:from-zinc-800 dark:to-zinc-900",
          "flex flex-col items-center justify-center"
        )}>
          <div className="w-20 h-20 rounded-full bg-white dark:bg-zinc-700 flex items-center justify-center mb-4 shadow-sm">
            <Camera className="w-10 h-10 text-[#98A2B3] dark:text-zinc-500" />
          </div>
          <p className={cn(baseTextSize, secondaryText, 'mb-1')}>
            {t('recipes.noImagesAvailable')}
          </p>
          <p className={cn(smallTextSize, secondaryText, 'mb-4')}>
            {t('recipes.addPhotosToShowcase')}
          </p>
          <button
            onClick={() => setIsManagingImages(true)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-xl",
              "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white font-semibold",
              "hover:bg-[#047857] transition-colors"
            )}
          >
            <Plus className="w-4 h-4" />
            <span>{t('recipes.uploadImages')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default RecipeImageCarousel;
